import { Box } from "lucide-react";
import { useCallback, useMemo } from "react";
import { DEFAULT_LOD, type ObjectEffectPresetKey } from "@mind-sands/shared";
import { getBoundingBox } from "../engine/meshRegistry";
import { useSceneStore } from "../stores/sceneStore";
import { useSelection } from "../hooks/useSelection";
import { useEditMode } from "../hooks/useEditMode";
import { useUiStore } from "../stores/uiStore";
import { useSceneObjects } from "../hooks/useSceneObjects";
import { useRecompileScene } from "../hooks/useRecompileScene";
import { getResolvedAssetUrl } from "../hooks/useAssetUrl";
import { toDisplayName } from "./objectInspector/shared";
import { TransformControls } from "./objectInspector/TransformControls";
import { ObjectModelInfo } from "./objectInspector/ObjectModelInfo";
import { ObjectBoundsPanel } from "./objectInspector/ObjectBoundsPanel";
import { LightPresetSelect } from "./objectInspector/LightPresetSelect";
import { ObjectEffectsSummary } from "./objectInspector/ObjectEffectsSummary";

/**
 * Object inspector panel showing transform, bounds, lighting, and effects
 * for the selected object. Displays when an object is selected in the scene.
 *
 * Composition root — delegates each section to a focused sub-component under
 * `./objectInspector/`.
 */
export function ObjectInspector() {
  const { selectedObjectId } = useSelection();
  const objects = useSceneStore((state) => state.scene.objects);
  const updateObject = useSceneStore((state) => state.updateObject);
  const activeSpaceDescriptor = useSceneStore(
    (state) => state.activeSpaceDescriptor,
  );
  const updateSpaceObjectEffects = useSceneStore(
    (state) => state.updateSpaceObjectEffects,
  );
  const recompile = useRecompileScene();
  const { removeObject } = useSceneObjects();
  const { isReadonly } = useEditMode();

  const currentLOD = useUiStore((state) =>
    selectedObjectId
      ? (state.lodMap.get(selectedObjectId) ?? DEFAULT_LOD)
      : DEFAULT_LOD,
  );

  const selectedObject = useMemo(
    () => objects.find((o) => o.id === selectedObjectId) ?? null,
    [objects, selectedObjectId],
  );

  const boundingBox = useMemo(() => {
    if (!selectedObject) return null;
    return getBoundingBox(selectedObject.id);
  }, [selectedObject]);

  // Index of the selected scene object — maps to the same index in the semantic
  // SpaceDescriptor.objects (the compiler preserves order: objects[N] → obj-N).
  const selectedIndex = useMemo(
    () => objects.findIndex((o) => o.id === selectedObjectId),
    [objects, selectedObjectId],
  );

  // Current semantic light preset authored on the space object (if any).
  const currentPreset = useMemo(() => {
    if (selectedIndex < 0) return "";
    return (
      activeSpaceDescriptor?.objects?.[selectedIndex]?.effects?.preset ?? ""
    );
  }, [activeSpaceDescriptor, selectedIndex]);

  // Resolve the loaded model URL to identify which file/LOD is rendering.
  const resolvedModelUrl = useMemo(() => {
    if (!selectedObject) return undefined;
    return getResolvedAssetUrl(
      `model:${selectedObject.objectId}:${currentLOD}`,
    );
  }, [selectedObject, currentLOD]);

  const handlePresetChange = useCallback(
    (value: string) => {
      if (selectedIndex < 0) return;
      updateSpaceObjectEffects(
        selectedIndex,
        value === "" ? undefined : { preset: value as ObjectEffectPresetKey },
      );
      void recompile();
    },
    [selectedIndex, updateSpaceObjectEffects, recompile],
  );

  const handleDelete = useCallback(() => {
    if (selectedObject) {
      void removeObject(selectedObject.id);
    }
  }, [selectedObject, removeObject]);

  if (isReadonly) {
    return null;
  }

  if (!selectedObject) {
    return (
      <div className="flex flex-col items-center justify-center py-8 px-4 gap-2">
        <Box className="w-8 h-8 text-sands-300" />
        <p className="text-xs text-sands-400 text-center">
          Select an object in the scene to inspect its properties
        </p>
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Object name */}
      <div className="mb-3 pb-2 border-b border-sands-100">
        <p className="text-sm font-medium text-sands-700">
          {toDisplayName(selectedObject.objectId)}
        </p>
        <p className="text-xs text-sands-400 mt-0.5">{selectedObject.id}</p>
      </div>

      <ObjectModelInfo
        objectId={selectedObject.objectId}
        lod={currentLOD}
        resolvedModelUrl={resolvedModelUrl}
      />

      <TransformControls
        object={selectedObject}
        onChange={(updates) => updateObject(selectedObject.id, updates)}
      />

      <ObjectBoundsPanel
        position={selectedObject.position}
        boundingBox={boundingBox}
      />

      <LightPresetSelect value={currentPreset} onChange={handlePresetChange} />

      <ObjectEffectsSummary
        archetype={selectedObject.archetype}
        effects={selectedObject.effects}
      />

      {/* Delete button */}
      <button
        onClick={handleDelete}
        className="w-full px-3 py-2 text-sm text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors"
      >
        Delete Object
      </button>
    </div>
  );
}
